import { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, Loader2, Lock, Copy, Pencil, Trash2, X, Check, RefreshCw, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import DeleteConfirmDialog from '../common/DeleteConfirmDialog';

// --- TYPE DEFINITIONS ---

interface DnsRecord {
  record_id: string;
  name: string;
  type: string;
  value: string;
  ttl: number | null;
  is_alias: boolean;
  is_system_managed: boolean;
  system_managed_reason?: string | null;
}

interface DnsRecordListResponse {
  domain_id: string;
  domain_name: string;
  records: DnsRecord[];
  truncated: boolean;
}

interface DnsRecordChangeResponse {
  status: string;
  change_id?: string | null;
  record?: DnsRecord | null;
  message: string;
}

// --- CONSTANTS ---

const RECORD_TYPES = ['A', 'AAAA', 'CNAME', 'MX', 'TXT', 'SRV', 'CAA'] as const;

const TTL_OPTIONS = [
  { value: 60, label: '1 minute' },
  { value: 300, label: '5 minutes' },
  { value: 1800, label: '30 minutes' },
  { value: 3600, label: '1 hour' },
  { value: 86400, label: '24 hours' },
];

const TYPE_PLACEHOLDERS: Record<string, string> = {
  A: '192.0.2.1',
  AAAA: '2001:db8::1',
  CNAME: 'target.example.com',
  MX: '10 smtp.google.com',
  TXT: 'v=spf1 include:_spf.google.com ~all',
  SRV: '10 5 5060 sip.example.com',
  CAA: '0 issue "letsencrypt.org"',
};

const TYPE_HINTS: Record<string, string> = {
  A: 'IPv4 address',
  AAAA: 'IPv6 address',
  CNAME: 'Target hostname. Only one CNAME is allowed per name.',
  MX: 'Priority followed by the mail server, e.g. "10 smtp.google.com"',
  TXT: 'Plain text value — quotes are added automatically',
  SRV: 'Priority, weight, port, target — e.g. "10 5 5060 sip.example.com"',
  CAA: 'Flags, tag, value — e.g. 0 issue "letsencrypt.org"',
};

// Light client-side validation; the server is authoritative
function validateValue(type: string, value: string): string | null {
  const v = value.trim();
  if (!v) return 'Value is required';
  switch (type) {
    case 'A':
      return /^\d{1,3}(\.\d{1,3}){3}$/.test(v) ? null : 'Enter a valid IPv4 address (e.g. 192.0.2.1)';
    case 'AAAA':
      return /^[0-9a-fA-F:]+$/.test(v) && v.includes(':') ? null : 'Enter a valid IPv6 address';
    case 'CNAME':
      return /^[a-zA-Z0-9_][a-zA-Z0-9_.-]*\.[a-zA-Z0-9_.-]+\.?$/.test(v) ? null : 'Enter a valid hostname (e.g. target.example.com)';
    case 'MX':
      return /^\d{1,5}\s+\S+$/.test(v) ? null : 'Format: priority followed by mail server (e.g. "10 smtp.google.com")';
    case 'SRV':
      return /^\d{1,5}\s+\d{1,5}\s+\d{1,5}\s+\S+$/.test(v) ? null : 'Format: priority weight port target (e.g. "10 5 5060 sip.example.com")';
    case 'CAA':
      return /^\d{1,3}\s+(issue|issuewild|iodef)\s+/.test(v) ? null : 'Format: flags tag value (e.g. 0 issue "letsencrypt.org")';
    default:
      return null;
  }
}

function formatTtl(ttl: number | null): string {
  if (ttl === null || ttl === undefined) return '—';
  const option = TTL_OPTIONS.find(o => o.value === ttl);
  if (option) return option.label;
  if (ttl % 3600 === 0) return `${ttl / 3600} h`;
  if (ttl % 60 === 0) return `${ttl / 60} min`;
  return `${ttl} s`;
}

// --- COMPONENT ---

const DnsRecordsPanel = ({ domainId, domainName }: { domainId: string; domainName: string }) => {
  const api = useAuthenticatedApi();

  const [records, setRecords] = useState<DnsRecord[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ type: 'A', name: '', value: '', ttl: 300 });
  const [addError, setAddError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ value: '', ttl: 300 });
  const [editError, setEditError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<DnsRecord | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [syncState, setSyncState] = useState<'syncing' | 'synced' | null>(null);
  const syncTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const relativeName = useCallback(
    (fqdn: string): string => {
      const suffix = `.${domainName.toLowerCase()}`;
      const name = fqdn.toLowerCase();
      if (name === domainName.toLowerCase()) return '@';
      return name.endsWith(suffix) ? name.slice(0, -suffix.length) : name;
    },
    [domainName]
  );

  const fetchRecords = useCallback(async () => {
    try {
      setError(null);
      const response = await api.get<DnsRecordListResponse>(apiEndpoints.domains.dnsRecords(domainId));
      setRecords(response.records);
      setTruncated(response.truncated);
    } catch (err: any) {
      setError(err.data?.detail || err.message || 'Failed to load DNS records');
    } finally {
      setLoading(false);
    }
  }, [api, domainId]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Clear any pending sync-poll timers on unmount
  useEffect(() => () => syncTimersRef.current.forEach(clearTimeout), []);

  // Poll the change status until INSYNC (max ~60s), showing a small indicator
  const trackChange = useCallback(
    (changeId?: string | null) => {
      if (!changeId) return;
      setSyncState('syncing');
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const response = await api.get<{ change_id: string; status: string }>(
            apiEndpoints.domains.dnsChangeStatus(domainId, changeId)
          );
          if (response.status === 'INSYNC') {
            setSyncState('synced');
            syncTimersRef.current.push(setTimeout(() => setSyncState(null), 4000));
            return;
          }
        } catch {
          // Status polling is best-effort; stop quietly on error
          setSyncState(null);
          return;
        }
        if (attempts < 12) {
          syncTimersRef.current.push(setTimeout(poll, 5000));
        } else {
          setSyncState(null);
        }
      };
      syncTimersRef.current.push(setTimeout(poll, 5000));
    },
    [api, domainId]
  );

  const handleAdd = async () => {
    const validationError = validateValue(addForm.type, addForm.value);
    if (validationError) {
      setAddError(validationError);
      return;
    }
    setAddError(null);
    setAdding(true);
    try {
      const response = await api.post<DnsRecordChangeResponse>(apiEndpoints.domains.dnsRecords(domainId), {
        name: addForm.name.trim() || '@',
        type: addForm.type,
        value: addForm.value.trim(),
        ttl: addForm.ttl,
      });
      toast.success(response.message || 'Record created');
      setAddForm({ type: addForm.type, name: '', value: '', ttl: addForm.ttl });
      setShowAddForm(false);
      trackChange(response.change_id);
      await fetchRecords();
    } catch (err: any) {
      setAddError(err.data?.detail || err.message || 'Failed to create record');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (record: DnsRecord) => {
    setEditingId(record.record_id);
    setEditForm({ value: record.value, ttl: record.ttl ?? 300 });
    setEditError(null);
    setDeleteTarget(null);
  };

  const handleSaveEdit = async (record: DnsRecord) => {
    const validationError = validateValue(record.type, editForm.value);
    if (validationError) {
      setEditError(validationError);
      return;
    }
    setEditError(null);
    setSavingId(record.record_id);
    try {
      const response = await api.put<DnsRecordChangeResponse>(
        apiEndpoints.domains.dnsRecord(domainId, record.record_id),
        { value: editForm.value.trim(), ttl: editForm.ttl }
      );
      toast.success(response.message || 'Record updated');
      setEditingId(null);
      trackChange(response.change_id);
      await fetchRecords();
    } catch (err: any) {
      setEditError(err.data?.detail || err.message || 'Failed to update record');
    } finally {
      setSavingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const record = deleteTarget;
    setDeleteTarget(null);
    setDeletingId(record.record_id);
    try {
      const response = await api.delete<DnsRecordChangeResponse>(apiEndpoints.domains.dnsRecord(domainId, record.record_id));
      toast.success(response.message || 'Record deleted');
      trackChange(response.change_id);
      await fetchRecords();
    } catch (err: any) {
      toast.error(err.data?.detail || err.message || 'Failed to delete record');
    } finally {
      setDeletingId(null);
    }
  };

  const copyValue = (value: string) => {
    navigator.clipboard.writeText(value);
    toast.success('Copied to clipboard');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-semantic-error/10 border border-semantic-error/30 text-semantic-error text-sm">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>{error}</span>
        <button onClick={() => { setLoading(true); fetchRecords(); }} className="ml-auto p-1 hover:bg-semantic-error/10 rounded" title="Retry">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <h6 className="font-medium text-neutral-900 dark:text-white text-sm">DNS Records</h6>
          {syncState === 'syncing' && (
            <span className="flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400">
              <Loader2 className="w-3 h-3 animate-spin" /> Syncing
            </span>
          )}
          {syncState === 'synced' && (
            <span className="flex items-center gap-1 text-xs text-semantic-success">
              <Check className="w-3 h-3" /> Synced
            </span>
          )}
        </div>
        {!showAddForm && (
          <button
            onClick={() => { setShowAddForm(true); setAddError(null); }}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-brand-primary/10 text-brand-primary hover:bg-brand-primary/20 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add record
          </button>
        )}
      </div>

      {truncated && (
        <p className="mb-3 text-xs text-neutral-500 dark:text-neutral-400">Showing the first 300 record sets in this zone.</p>
      )}

      {showAddForm && (
        <div className="mb-4 p-3 bg-white dark:bg-neutral-800 rounded-lg border border-neutral-200 dark:border-neutral-700">
          <div className="grid grid-cols-1 sm:grid-cols-[6rem_1fr_8rem] gap-2 mb-2">
            <select
              value={addForm.type}
              onChange={e => { setAddForm({ ...addForm, type: e.target.value }); setAddError(null); }}
              className="px-2 py-1.5 text-sm rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white"
            >
              {RECORD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <div className="flex items-center rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 overflow-hidden">
              <input
                type="text"
                value={addForm.name}
                onChange={e => setAddForm({ ...addForm, name: e.target.value })}
                placeholder="@"
                className="flex-1 min-w-0 px-2 py-1.5 text-sm bg-transparent text-neutral-900 dark:text-white focus:outline-none"
              />
              <span className="px-2 text-xs text-neutral-400 dark:text-neutral-500 whitespace-nowrap">.{domainName}</span>
            </div>
            <select
              value={addForm.ttl}
              onChange={e => setAddForm({ ...addForm, ttl: Number(e.target.value) })}
              className="px-2 py-1.5 text-sm rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white"
            >
              {TTL_OPTIONS.map(o => <option key={o.value} value={o.value}>TTL: {o.label}</option>)}
            </select>
          </div>
          {addForm.type === 'TXT' ? (
            <textarea
              value={addForm.value}
              onChange={e => setAddForm({ ...addForm, value: e.target.value })}
              placeholder={TYPE_PLACEHOLDERS[addForm.type]}
              rows={2}
              className="w-full px-2 py-1.5 text-sm font-mono rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white"
            />
          ) : (
            <input
              type="text"
              value={addForm.value}
              onChange={e => setAddForm({ ...addForm, value: e.target.value })}
              placeholder={TYPE_PLACEHOLDERS[addForm.type]}
              className="w-full px-2 py-1.5 text-sm font-mono rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white"
            />
          )}
          <p className="mt-1.5 text-xs text-neutral-500 dark:text-neutral-400">
            {TYPE_HINTS[addForm.type]} · Use @ or leave the name empty for the domain root. TTL applies to all records with the same name and type.
          </p>
          {addError && <p className="mt-1.5 text-xs text-semantic-error">{addError}</p>}
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => { setShowAddForm(false); setAddError(null); }}
              className="px-2.5 py-1.5 text-xs font-medium rounded-lg text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={adding || !addForm.value.trim()}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-brand-primary text-neutral-900 hover:bg-brand-primary/90 disabled:opacity-50 transition-colors"
            >
              {adding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Add record
            </button>
          </div>
        </div>
      )}

      {records.length === 0 ? (
        <p className="text-sm text-neutral-500 dark:text-neutral-400 py-2">No DNS records found.</p>
      ) : (
        <div className="rounded-lg border border-neutral-200 dark:border-neutral-700 divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden">
          {records.map((record, index) => {
            const rowKey = record.record_id || `system-${index}`;
            const isEditing = editingId === record.record_id && record.record_id !== '';
            return (
              <div key={rowKey} className="px-3 py-2 bg-white dark:bg-neutral-800 text-sm">
                <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-3">
                  <span className="inline-flex w-14 flex-shrink-0 justify-center px-1.5 py-0.5 text-xs font-semibold rounded bg-neutral-100 dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200">
                    {record.type}
                  </span>
                  <span className="w-32 flex-shrink-0 font-mono text-xs text-neutral-900 dark:text-white truncate" title={record.name}>
                    {relativeName(record.name)}
                  </span>
                  {isEditing ? (
                    <div className="flex-1 flex flex-col sm:flex-row gap-2 sm:items-center">
                      <input
                        type="text"
                        value={editForm.value}
                        onChange={e => setEditForm({ ...editForm, value: e.target.value })}
                        className="flex-1 min-w-0 px-2 py-1 text-xs font-mono rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white"
                      />
                      <select
                        value={editForm.ttl}
                        onChange={e => setEditForm({ ...editForm, ttl: Number(e.target.value) })}
                        className="px-2 py-1 text-xs rounded-md border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white"
                      >
                        {TTL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleSaveEdit(record)}
                          disabled={savingId === record.record_id}
                          className="p-1.5 rounded-md text-semantic-success hover:bg-semantic-success/10"
                          title="Save"
                        >
                          {savingId === record.record_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => { setEditingId(null); setEditError(null); }}
                          className="p-1.5 rounded-md text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0 flex items-center gap-1.5">
                        <code className="font-mono text-xs text-neutral-700 dark:text-neutral-300 truncate" title={record.value}>
                          {record.value}
                        </code>
                        <button onClick={() => copyValue(record.value)} className="p-1 text-neutral-400 hover:text-brand-primary flex-shrink-0" title="Copy value">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <span className="w-20 flex-shrink-0 text-xs text-neutral-500 dark:text-neutral-400">{formatTtl(record.ttl)}</span>
                      {record.is_system_managed ? (
                        <span
                          className="flex items-center gap-1 flex-shrink-0 px-1.5 py-0.5 text-xs rounded bg-neutral-100 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400"
                          title={record.system_managed_reason || 'Managed by Kolega'}
                        >
                          <Lock className="w-3 h-3" /> Managed by Kolega
                        </span>
                      ) : (
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            onClick={() => startEdit(record)}
                            className="p-1.5 rounded-md text-neutral-500 hover:text-brand-primary hover:bg-neutral-100 dark:hover:bg-neutral-700"
                            title="Edit record"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => setDeleteTarget(record)}
                            disabled={deletingId === record.record_id}
                            className="p-1.5 rounded-md text-neutral-500 hover:text-semantic-error hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                            title="Delete record"
                          >
                            {deletingId === record.record_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
                {isEditing && editError && <p className="mt-1.5 text-xs text-semantic-error">{editError}</p>}
              </div>
            );
          })}
        </div>
      )}

      <DeleteConfirmDialog
        open={!!deleteTarget}
        title="Delete DNS record?"
        message={
          deleteTarget
            ? `The ${deleteTarget.type} record at ${relativeName(deleteTarget.name)} with value "${
                deleteTarget.value.length > 60 ? `${deleteTarget.value.slice(0, 60)}…` : deleteTarget.value
              }" will be removed. Services relying on it (like email) may stop working.`
            : ''
        }
        onConfirm={confirmDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default DnsRecordsPanel;
